import React from 'react';
import { OrderSelector as UIOrderSelector } from '@shopee/chatbot-ui';
import {
  OrderSelectorProps as UIOrderSelectorProps,
  OrderType as UIOrderType,
  ButtonType,
} from '@shopee/chatbot-ui/lib/components/OrderSelector';
import {
  INPUT_TYPE,
  FoodOrderBannerType,
  OrderType,
  CUSTOMER_HIDDEN_TEXT,
  TRACKING_SECTION,
  TRACKING_TARGET,
  TRACKING_MSG_TYPE,
  TRACKING_TYPE,
  GROUP_ORDER_TEXT,
  dirverOrderStatusText,
  TRACKING_BOOLEAN,
} from '@/utils/constants';
import { ChatbotUIBasicProps } from '@/types/common';
import { USER_SEND_TYPE } from '@/types/history';
import dateLib from '@/utils/dateLib/moment';
import { assignParams } from '@/utils/dataTypeConvert';
import { getTrans } from '@/utils/instantLoaded/tools';
import API from '@/services';
import { sessionStore } from '@/stores';
import { getDriverOrderStatusTrans } from './PropsProtocol/DriverOrderListPopup';
import { isDriverAndFoodUIOptimisation } from '@/utils/experiment/getDriverAndFoodUIOptimisationExperiment';
import { getDriverOrderCode } from './OrderCardWithButton';
import { FoodOrderServiceType } from '@shopee/chatbot-ui/lib/components/FoodOrder';

export type OrderListPopupProps = UIOrderSelectorProps & {
  // selectOtherAction: any;
  selectAction: any;
  content?: any;
  useingInTFE?: boolean;
  text: string;
  groupOrderText?: string;
  btn: {
    text_value: string;
    callback_params: Record<string, unknown>;
    uri_key: string;
    button_type: ButtonType;
  };
  orderType: UIOrderType;
  taskbotInfo?: any;
  node_id: string;
} & ChatbotUIBasicProps;

interface loadMoreParams {
  [name: string]: number;
  page_num: number;
  page_size: number;
  status: number;
}

const OrderSelector = (props: OrderListPopupProps) => {
  const {
    selectAction,
    useingInTFE = false,
    text,
    orderType,
    btn,
    taskbotInfo,
    node_id,
    panelTitleText,
    groupOrderText = getTrans(GROUP_ORDER_TEXT),
  } = props;

  const generateOrder = (orderItem: any, orderTaskbotInfo: any) => {
    let nextParams = null;
    const hideBuyerInfoStatus = ['Delivered', 'Cancelled'];
    const buyerInfo = hideBuyerInfoStatus.includes(orderItem.delivery_status)
      ? getTrans(CUSTOMER_HIDDEN_TEXT)
      : `${orderItem.delivery_location?.address}`;

    const orderInfo = {
      orderId: orderItem.order_id,
      bannerType: orderItem.bannerType,
      status: getDriverOrderStatusTrans(orderItem.delivery_status as keyof typeof dirverOrderStatusText),
      shopName:
        orderItem.service_type === FoodOrderServiceType.Food ? orderItem.shop_name : orderItem.pickup_location?.address,
      buyerInfo,
      orderCode: getDriverOrderCode(orderItem),
      createTime: dateLib.format(orderItem.create_time, 'HH:ii DD MMM YYYY'),
      serviceType: orderItem.service_type,
      isUIOptimisation: isDriverAndFoodUIOptimisation(),
    };

    if (useingInTFE) {
      nextParams = {
        session_id: props.sessionId,
        dialogue_id: props.dialogueId,
        thread_id: props.threadId,
        input_type: INPUT_TYPE.BUTTON,
        thread_node_id: props.threadNodeId,
        is_bypass: props.selectAction?.is_bypass,
        message_text: orderItem?.order_id?.toString(),
        message_extra: {
          task_bot_action: {
            ...(selectAction || {}),
            ...{
              params: assignParams(props.selectAction?.params, {
                driver_delivery_id: orderItem.driver_delivery_id,
                order_id_string: orderItem?.query_callback?.driver_order_callback?.order_id,
                service_type: orderItem.service_type,
                delivery_id: orderItem.delivery_id,
              }),
            },
            task_bot_info: orderTaskbotInfo,
          },
          params: {
            // history message
            user_send: {
              type: USER_SEND_TYPE.ORDER_SELECT,
              order_select: {
                ...orderItem,
                banner_type: orderItem.bannerType,
                buyer_info: buyerInfo,
                order_type: orderType,
                delivery_status: orderInfo.status,
                pickup_seq: orderItem.pickup_seq?.toString(),
              },
            },
          },
        },
      };
    } else {
      nextParams = {
        session_id: props.sessionId,
        dialogue_id: props.dialogueId,
        input_type: INPUT_TYPE.INPUT_BY_CLICK_ORDER,
        message_text: orderItem?.order_id?.toString(),
        message_extra: {
          node_id,
          params: {
            order_id: orderItem?.order_id,
            user_send: {
              type: USER_SEND_TYPE.ORDER_SELECT,
              driver_order: orderItem?.query_callback?.driver_order_callback,
              order_select: {
                ...orderItem,
                order_type: orderType,
                banner_type: orderItem.bannerType,
                buyer_info: buyerInfo,
                delivery_status: orderInfo.status,
                pickup_seq: orderItem.pickup_seq?.toString(),
              },
            },
          },
        },
      };
    }

    return {
      ...orderInfo,
      action: {
        info: {
          ...orderItem,
          ...orderInfo,
        },
        func: 'showOrder',
        nextParams,
        orderType,
      },
    };
  };

  const onLoadMore = async (loadMoreProps: loadMoreParams) => {
    if (loadMoreProps.page_num !== 0) {
      return { data: [] };
    }

    if (orderType === OrderType.food) {
      const res = await (API as { queryOrderList: Function })
        .queryOrderList({ ...(btn.callback_params || {}) })
        .catch(() => ({}));

      const deliveryList = res?.food_delivery_list || [];
      const orderList = deliveryList.map((deliveryItem: any) => {
        const isGroupOrder = deliveryItem.union_deliveries.length > 1;

        if (isGroupOrder) {
          return {
            groupOrders: deliveryItem.union_deliveries.map((order: any) =>
              generateOrder({ bannerType: FoodOrderBannerType.OrderCode, ...order }, taskbotInfo),
            ),
          };
        }

        return generateOrder(
          { bannerType: FoodOrderBannerType.CreateTime, ...deliveryItem.union_deliveries[0] },
          taskbotInfo,
        );
      });
      return { data: orderList };
    }

    return { data: [] };
  };

  const { isHistory, nodePointId, taskbotName, historySessionId, sessionId, historyDialogueId, dialogueId } = props;

  const trackingProps = {
    componentTrackingData: {
      selectorBtn: {
        trackingsection:
          TRACKING_SECTION[useingInTFE ? TRACKING_TYPE.TFE_ORDER_SELECTOR : TRACKING_TYPE.ORDER_SELECTOR],
        trackingtarget: TRACKING_TARGET[useingInTFE ? TRACKING_TYPE.TFE_ORDER_SELECTOR : TRACKING_TYPE.ORDER_SELECTOR],
        trackingdataBasic: {
          chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
          chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
          is_change_order_option: TRACKING_BOOLEAN.Yes,
        },
      },
      panelOrder: {
        trackingsection: TRACKING_SECTION[useingInTFE ? TRACKING_TYPE.ORDER_LIST : TRACKING_TYPE.GROUP_ORDER_LIST],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.PANELORDER],
        trackingdataBasic: {
          node_point_id: props.nodePointId,
          kb_bot_id: props.kbBotId,
          tf_node_id: props.tfNodeId,
          tf_case_id: props.tfCaseId,
          chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
          chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
          task_bot_name: props.taskbotName,
          if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
          origin: 'List',
          taskbot_id: props.taskbotInfo?.task_bot_id,
          taskflow_id: props.taskbotInfo?.flow_id,
        },
        trackingItemData: (data: any) => ({
          location: data?.index,
          orderid: data?.orderId,
          order_status: data?.status,
        }),
      },
      closePanel: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.ORDER_LIST],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.ORDER_LIST_CLOSEPANEL],
        trackingdataBasic: {
          chat_session_id: isHistory ? historySessionId : sessionId,
          chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
          node_point_id: nodePointId,
          if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
          task_bot_name: taskbotName,
          taskbot_id: props.taskbotInfo?.task_bot_id,
          taskflow_id: props.taskbotInfo?.flow_id,
          kb_bot_id: props.kbBotId,
          tf_node_id: props.tfNodeId,
          tf_case_id: props.tfCaseId,
        },
      },
      panelOrderScroll: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.ORDER_LIST],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.ORDER_LIST],
        trackingdataBasic: {
          chat_session_id: isHistory ? historySessionId : sessionId,
          chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
          node_point_id: nodePointId,
          if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
          task_bot_name: taskbotName,
          taskbot_id: props.taskbotInfo?.task_bot_id,
          taskflow_id: props.taskbotInfo?.flow_id,
          kb_bot_id: props.kbBotId,
          tf_node_id: props.tfNodeId,
          tf_case_id: props.tfCaseId,
        },
      },
    },
  };

  return (
    <UIOrderSelector
      {...trackingProps}
      text={text}
      buttonText={btn.text_value}
      onLoadMore={onLoadMore}
      limit={50}
      orderCardType={orderType}
      buttonType={btn.button_type}
      disabled={props.isHistory}
      groupOrderText={groupOrderText}
      panelTitleText={panelTitleText}
      handleSelectOrder={(order) => {
        if (order?.groupOrders) {
          order.groupOrders.forEach((groupOrder: { orderId: any }) => {
            sessionStore.ADD_NEW_ORDER_SN(groupOrder?.orderId);
          });
        } else {
          sessionStore.ADD_NEW_ORDER_SN(order?.action?.info?.order_id_string);
        }
      }}
      isUIOptimisation={isDriverAndFoodUIOptimisation()}
    />
  );
};

export default OrderSelector;
